import { Dialog, DialogProps } from '@mui/material';
import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MuiModalContextProvider, OpenModalOptions } from './ModalContext';

interface MuiModalProviderOptions {
  children: React.ReactNode;
}

interface ModalObject {
  modalId: string;
  modalLabel?: string;
  modalProps: Omit<DialogProps, 'open' | 'children'> & {
    shouldDisableBackdropClick?: boolean;
  };
  component: () => JSX.Element;
}

export function MuiModalProvider({ children }: MuiModalProviderOptions) {
  const [modals, setModals] = useState<ModalObject[]>([]);

  const openModal = useCallback(
    ({ contentComponent, modalProps }: OpenModalOptions) => {
      const modalId = uuidv4();

      setModals((modalsState) => [
        ...modalsState,
        {
          modalId,
          modalLabel: modalProps.id,
          modalProps,
          component: contentComponent,
        },
      ]);

      return modalId;
    },
    [],
  );

  const closeModal = useCallback((modalId?: string) => {
    setModals((modalsState) => {
      if (modalId) {
        return modalsState.filter((modal) => modal.modalId !== modalId);
      }

      modalsState.pop();
      return [...modalsState];
    });
  }, []);

  const renderModals = () => (
    <>
      {modals.map(({ modalId, component: Component, modalProps }) => {
        const { shouldDisableBackdropClick, ...props } = modalProps;

        function modalCloseHandler(eve: SyntheticEvent, reason: any) {
          if (!shouldDisableBackdropClick) {
            props.onClose?.(eve, reason);
            closeModal(modalId);
          }
        }

        return (
          <React.Fragment key={modalId}>
            <Dialog
              {...props}
              PaperProps={{
                elevation: 3,
                ...(props?.PaperProps ?? {}),
              }}
              disableRestoreFocus={props.disableRestoreFocus ?? true}
              id={modalId}
              onClose={modalCloseHandler}
              open={true}
            >
              <Component />
            </Dialog>
          </React.Fragment>
        );
      })}
    </>
  );

  const activeModalId = modals.length
    ? modals[modals.length - 1].modalId
    : undefined;

  const activeModalLabel = modals.length
    ? modals[modals.length - 1].modalLabel
    : undefined;

  const modalContextValue = useMemo(
    () => ({
      openModal,
      closeModal,
      activeModalId,
      activeModalLabel,
    }),
    [activeModalId, activeModalLabel, closeModal, openModal],
  );

  return (
    <MuiModalContextProvider value={modalContextValue}>
      {children}
      {renderModals()}
    </MuiModalContextProvider>
  );
}
